/**
 * Chat context: Socket.IO connection, presence, contacts, conversations, messages.
 * Multi-tenant: sends unidade in API calls; socket auth uses token.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { io } from 'socket.io-client';
import { getToken } from '../api/client';
import { apiFetch } from '../api/client';
import API_CONFIG from '../config/api';
import { playNotificationSound } from '../utils/notificationSound';

const ChatContext = createContext(null);

const STATUS_OPTIONS = [
  { value: 'available', label: 'Disponível' },
  { value: 'busy', label: 'Ocupado' },
  { value: 'away', label: 'Ausente' },
  { value: 'offline', label: 'Offline' }
];

export function ChatProvider({ children, user, fotoPerfil, unidadeSelecionada }) {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [status, setStatusState] = useState('available');
  const [contacts, setContacts] = useState([]);
  const [presenceMap, setPresenceMap] = useState({});
  const [openConversations, setOpenConversations] = useState([]);
  const [messagesByConv, setMessagesByConv] = useState({});
  const [typingMap, setTypingMap] = useState({});
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isChatOpen, setChatOpen] = useState(false);
  // FASE 1 — layout Centro de Comunicações (Dual-Pane / overlay)
  const [isExpanded, setIsExpanded] = useState(false); // overlay tela cheia (FASE 2: Dual-Pane Wrapper)
  const [activeChatId, setActiveChatId] = useState(null); // conversa no painel direito (FASE 2: ChatWindow embutido + mídia)
  const openConvsRef = useRef([]);
  openConvsRef.current = openConversations;
  const joinedConversationsRef = useRef(new Set());

  const unreadCount = useMemo(() => {
    if (!user?.id) return 0;
    let count = 0;
    for (const convId in messagesByConv) {
      const msgs = messagesByConv[convId] || [];
      for (const m of msgs) {
        if (m.senderId !== user.id && !m.readAt) count++;
      }
    }
    return Math.min(count, 99);
  }, [messagesByConv, user?.id]);

  const token = getToken();
  const unidadeCodigo = unidadeSelecionada?.codigo;

  useEffect(() => {
    if (!user?.id || !token) return;
    const s = io(API_CONFIG.BACKEND_URL, {
      path: '/socket.io',
      auth: {
        token,
        status: status
      },
      transports: ['websocket', 'polling']
    });
    s.on('connect', () => {
      setConnected(true);
    });
    s.on('disconnect', (reason) => {
      setConnected(false);
    });
    s.on('connect_error', (err) => {
      console.warn('[Chat] connect_error', err.message);
      setConnected(false);
    });
    s.on('presence:update', (payload) => {
      setPresenceMap((prev) => ({
        ...prev,
        [payload.userId]: {
          status: payload.status,
          lastSeenAt: payload.lastSeenAt,
          nome: payload.nome
        }
      }));
    });
    // Notificação sonora: mensagens de outros usuários (já ativo — FASE 1)
    s.on('message:new', (msg) => {
      if (msg.senderId !== user?.id) {
        playNotificationSound();
      }
      setMessagesByConv((prev) => ({
        ...prev,
        [msg.conversationId]: [...(prev[msg.conversationId] || []), msg]
      }));
    });
    s.on('message:delivered', (payload) => {
      setMessagesByConv((prev) => {
        const list = prev[payload.conversationId] || [];
        return {
          ...prev,
          [payload.conversationId]: list.map((m) =>
            m.id === payload.messageId ? { ...m, deliveredAt: new Date().toISOString() } : m
          )
        };
      });
    });
    s.on('message:read', (payload) => {
      setMessagesByConv((prev) => {
        const list = prev[payload.conversationId] || [];
        const ids = new Set(payload.messageIds || []);
        return {
          ...prev,
          [payload.conversationId]: list.map((m) =>
            ids.has(m.id) ? { ...m, readAt: new Date().toISOString() } : m
          )
        };
      });
    });
    s.on('typing:start', (payload) => {
      setTypingMap((prev) => ({ ...prev, [payload.conversationId]: payload.userName }));
    });
    s.on('typing:stop', (payload) => {
      setTypingMap((prev) => {
        const next = { ...prev };
        delete next[payload.conversationId];
        return next;
      });
    });
    setSocket(s);
    return () => {
      s.close();
      setSocket(null);
      setConnected(false);
    };
  }, [user?.id, token]);

  useEffect(() => {
    if (socket && connected) {
      socket.emit('presence:update', { status });
    }
  }, [socket, connected, status]);

  const fetchContacts = useCallback(async () => {
    if (!unidadeCodigo) return;
    setLoadingContacts(true);
    try {
      const params = new URLSearchParams();
      params.set('unidade', unidadeCodigo);
      const res = await apiFetch(`/api/chat/contacts?${params.toString()}`);
      const data = await res.json();
      if (res.ok) setContacts(Array.isArray(data) ? data : []);
      else setContacts([]);
    } catch (e) {
      setContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  }, [unidadeCodigo]);

  useEffect(() => {
    if (connected && unidadeCodigo) {
      fetchContacts();
    }
  }, [connected, unidadeCodigo, fetchContacts]);

  const fetchGroups = useCallback(async () => {
    if (!unidadeCodigo) return;
    setLoadingGroups(true);
    try {
      const params = new URLSearchParams();
      params.set('unidade', unidadeCodigo);
      const res = await apiFetch(`/api/chat/conversations?${params.toString()}`);
      const data = await res.json();
      if (res.ok) setGroups(Array.isArray(data) ? data : []);
      else setGroups([]);
    } catch (e) {
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [unidadeCodigo]);

  useEffect(() => {
    if (connected && unidadeCodigo) {
      fetchGroups();
    }
  }, [connected, unidadeCodigo, fetchGroups]);

  useEffect(() => {
    if (!socket || !connected || !groups || groups.length === 0) return;

    const joined = joinedConversationsRef.current;
    groups.forEach((conv) => {
      if (!conv || !conv.id) return;
      if (!joined.has(conv.id)) {
        socket.emit('conv:join', conv.id);
        joined.add(conv.id);
      }
    });
  }, [socket, connected, groups]);

  const setStatus = useCallback((newStatus) => {
    setStatusState(newStatus);
  }, []);

  const getOrCreateConversation = useCallback(
    async (otherUserId) => {
      if (!unidadeCodigo) return null;
      const res = await apiFetch(
        `/api/chat/conversations/direct/${otherUserId}?unidade=${encodeURIComponent(unidadeCodigo)}`
      );
      const data = await res.json();
      if (!res.ok) return null;
      return data;
    },
    [unidadeCodigo]
  );

  const fetchMessages = useCallback(async (conversationId, before) => {
    const params = new URLSearchParams();
    if (before) params.set('before', before);
    params.set('limit', '50');
    const res = await apiFetch(`/api/chat/conversations/${conversationId}/messages?${params.toString()}`);
    const data = await res.json();
    if (!res.ok) return [];
    return Array.isArray(data) ? data : [];
  }, []);

  const createGroupConversation = useCallback(
    async (name, participantIds) => {
      if (!unidadeCodigo) return null;
      const res = await apiFetch('/api/chat/conversations/group', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: (name || '').trim() || 'Grupo', participantIds: participantIds || [] })
      });
      const data = await res.json();
      if (!res.ok) return null;
      return data;
    },
    [unidadeCodigo]
  );

  const openConversation = useCallback(
    async (contact) => {
      const conv = await getOrCreateConversation(contact.id);
      if (!conv) return null;
      const existing = openConversations.find((c) => c.id === conv.id);
      if (existing) {
        setActiveChatId(existing.id); // Dual-Pane: painel direito (FASE 2)
        return existing;
      }
      const messages = await fetchMessages(conv.id);
      setMessagesByConv((prev) => ({ ...prev, [conv.id]: messages }));
      const item = {
        id: conv.id,
        type: 'direct',
        contact,
        name: null,
        messages: messages
      };
      setOpenConversations((prev) => [...prev.filter((c) => c.id !== conv.id), item]);
      setActiveChatId(conv.id);
      if (socket) socket.emit('conv:join', conv.id);
      return item;
    },
    [getOrCreateConversation, openConversations, fetchMessages, socket]
  );

  const openGroupConversation = useCallback(
    async (group) => {
      const existing = openConversations.find((c) => c.id === group.id);
      if (existing) {
        setActiveChatId(existing.id);
        return existing;
      }
      const messages = await fetchMessages(group.id);
      setMessagesByConv((prev) => ({ ...prev, [group.id]: messages }));
      const item = {
        id: group.id,
        type: 'group',
        contact: null,
        name: group.name || 'Grupo',
        messages: messages
      };
      setOpenConversations((prev) => [...prev.filter((c) => c.id !== group.id), item]);
      setActiveChatId(group.id);
      if (socket) socket.emit('conv:join', group.id);
      return item;
    },
    [openConversations, fetchMessages, socket]
  );

  const closeConversation = useCallback((conversationId) => {
    setOpenConversations((prev) => prev.filter((c) => c.id !== conversationId));
    setActiveChatId((prev) => (prev === conversationId ? null : prev));
    setTypingMap((prev) => {
      const next = { ...prev };
      delete next[conversationId];
      return next;
    });
  }, []);

  const sendMessage = useCallback(
    (conversationId, payload, ack) => {
      if (!conversationId) {
        if (ack) ack({ error: 'Conversa não definida.' });
        return;
      }

      // Adicionar mensagem localmente para aparecer imediatamente
      const now = new Date().toISOString();
      const tempId = `local-${conversationId}-${Date.now()}`;
      const localMessage = {
        id: tempId,
        conversationId,
        senderId: user?.id,
        senderName: user?.nome || user?.email || 'Você',
        type: payload?.type || 'text',
        content: payload?.content ?? null,
        fileUrl: payload?.fileUrl,
        fileName: payload?.fileName,
        fileSize: payload?.fileSize,
        createdAt: now,
        pending: true
      };

      setMessagesByConv((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), localMessage]
      }));

      if (!socket) {
        if (ack) ack({ error: 'Não conectado.' });
        return;
      }

      socket.emit('message:send', { conversationId, ...payload }, (response) => {
        // Se o backend retornar a mensagem persistida, podemos substituir a local
        if (response && response.message) {
          const serverMsg = response.message;
          setMessagesByConv((prev) => {
            const list = prev[conversationId] || [];
            const idx = list.findIndex((m) => m.id === tempId);
            if (idx === -1) {
              return {
                ...prev,
                [conversationId]: [...list, serverMsg]
              };
            }
            const nextList = [...list];
            nextList[idx] = serverMsg;
            return {
              ...prev,
              [conversationId]: nextList
            };
          });
        }
        if (typeof ack === 'function') {
          ack(response);
        }
      });
    },
    [socket, user?.id, user?.nome, user?.email, setMessagesByConv]
  );

  const markDelivered = useCallback(
    (conversationId, messageId) => {
      if (socket) socket.emit('message:delivered', { conversationId, messageId });
    },
    [socket]
  );

  const markRead = useCallback(
    (conversationId, messageIds) => {
      if (socket) socket.emit('message:read', { conversationId, messageIds: Array.isArray(messageIds) ? messageIds : [messageIds] });
    },
    [socket]
  );

  const sendTypingStart = useCallback(
    (conversationId) => {
      if (socket) socket.emit('typing:start', { conversationId });
    },
    [socket]
  );

  const sendTypingStop = useCallback(
    (conversationId) => {
      if (socket) socket.emit('typing:stop', { conversationId });
    },
    [socket]
  );

  const value = {
    connected,
    status,
    setStatus,
    STATUS_OPTIONS,
    contacts,
    presenceMap,
    loadingContacts,
    fetchContacts,
    openConversations,
    openConversation,
    openGroupConversation,
    createGroupConversation,
    groups,
    loadingGroups,
    fetchGroups,
    closeConversation,
    messagesByConv,
    setMessagesByConv,
    fetchMessages,
    sendMessage,
    markDelivered,
    markRead,
    typingMap,
    sendTypingStart,
    sendTypingStop,
    socket,
    user,
    fotoPerfil,
    isChatOpen,
    setChatOpen,
    // FASE 1 — viewport Dual-Pane (FASE 2: overlay + ChatWindow no painel direito + mídia)
    isExpanded,
    setIsExpanded,
    activeChatId,
    setActiveChatId,
    unreadCount,
    setUnreadCount: () => {} // mantido para compatibilidade; unreadCount é derivado de messagesByConv
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  return ctx;
}
